// In-memory cache for the client page's project list response.
//
// Keyed by clientId + a serialized hash of the request payload (filters,
// sort, includeArchived) since changing any of those produces a different
// server-side result. TTL is short; cache is invalidated when an order
// changes (call invalidateClientProjectLists from save flows). In-memory
// only — page refresh clears it.

import { cloneCacheValue } from './cache-utils';

const DEFAULT_TTL_MS = 60000;

interface ProjectListCacheEntry {
  data: any;
  fetchedAt: number;
}

const cache = new Map<string, ProjectListCacheEntry>();

function makeKey(clientId: number, payload: Record<string, any>): string {
  return `${clientId}::${JSON.stringify(payload)}`;
}

export function getProjectListCache(
  clientId: number,
  payload: Record<string, any>,
  ttlMs: number = DEFAULT_TTL_MS
): any | null {
  if (!clientId) return null;
  const entry = cache.get(makeKey(clientId, payload));
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > ttlMs) {
    cache.delete(makeKey(clientId, payload));
    return null;
  }
  return cloneCacheValue(entry.data);
}

export function setProjectListCache(
  clientId: number,
  payload: Record<string, any>,
  data: any
): void {
  if (!clientId) return;
  cache.set(makeKey(clientId, payload), { data: cloneCacheValue(data), fetchedAt: Date.now() });
}

export function invalidateClientProjectLists(clientId: number): void {
  if (!clientId) {
    cache.clear();
    return;
  }
  const prefix = `${clientId}::`;
  for (const key of Array.from(cache.keys())) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
}

export function clearAllProjectListCache(): void {
  cache.clear();
}
